'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import InlineSVG from '@src/components/Shared/InlineSVG';
import NextImage from '@src/components/Shared/NextImage';
import ProductBox from '@src/components/Shared/ProductBox';

import Faq from '@src/components/Sections/Faq';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';
import ImageTextTertiary from '@src/components/Sections/ImageTextTertiary';
import HeroValdProd from '@src/components/Sections/HeroValdProd';
import VideoTextPrimary from '@src/components/Sections/VideoTextPrimary';

import usePreviewContent from '@src/hooks/usePreviewContent';

import { ForceFramePageClientPropTypes } from './ForceFramePage.types';
import constructURL from '@src/utils/constructURL';
import HotSpots from '@src/components/Sections/HotSpots';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

const ForceFramePageClient: React.FC<ForceFramePageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/forceframe`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='-mt-[4.5rem]! mb-[-4rem]! md:mt-[6.5rem]! lg:mb-[8rem]!'
        imgClassName='mt-[2rem] w-[85%] mx-auto md:absolute md:right-[0] md:bottom-[-5rem] md:w-[50rem] lg:bottom-[-4rem] lg:relative lg:w-[60rem] xl:left-[-15rem] xl:w-[70rem] xl:left-[0rem] 2xl:w-[70rem] 2xl:bottom-[0] xsm:min-h-[29.1297rem] md:min-h-[38.6297rem] lg:min-h-[39.6375rem] 2xl:min-h-[49.5641rem] 3xl:min-h-[54.0844rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[30rem] mb-[2.4rem] md:mt-[4.1rem] lg:max-w-[50rem] xsm:min-h-[5.6406rem] lg:min-h-[7.2031rem] 2xl:min-h-[9.4rem]'
        introClassName='[&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.3rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%] lg:mt-[-190px]'
      />

      <VideoTextPrimary
        id={page?.strengthTestingAndTraining?.items?.[0]?.scrollTo?.slice(1)}
        className='3xl:pb-[6.5rem]! my-[4rem]! lg:my-[0]!'
        videoClassName='order-1 lg:order-1 lg:w-6/12'
        textContainerClassName='lg:w-6/12 mt-[5rem] lg:mt-[0]'
        videoURL={page?.strengthTestingAndTraining?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.strengthTestingAndTraining?.items?.[0]?.preTitle}
        title={page?.strengthTestingAndTraining?.items?.[0]?.title}
        titleClassName='lg:max-w-[50rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[60rem]'
              bodyCopy={page?.strengthTestingAndTraining?.items?.[0]?.bodyCopy?.json?.content}
            />

            <div className='flex items-center justify-start gap-3'>
              <RenderButtons
                className='w-full'
                items={page?.strengthTestingAndTraining?.items?.[0]?.buttonCollection?.items}
              />
            </div>
          </>
        }
      />

      <ImageTextTertiary
        id={page?.modularity?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='py-[3rem] lg:mb-[0]!'
        title={page?.modularity?.items?.[0]?.title}
        titleClassName='lg:max-w-[60rem]'
        pretitle={page?.modularity?.items?.[0]?.preTitle}
        imgURL={page?.modularity?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.modularity?.items?.[0]?.mediaPrimary?.description}
        aspectRatio={
          page?.modularity?.items?.[0]?.mediaPrimary?.width / page?.modularity?.items?.[0]?.mediaPrimary?.height
        }
        width={page?.modularity?.items?.[0]?.mediaPrimary?.width}
        imgSecondClassName='lg:w-[70%]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[60rem]'
              bodyCopy={page?.modularity?.items?.[0]?.bodyCopy?.json?.content}
            />

            <div className='flex items-center justify-start gap-3'>
              <RenderButtons className='w-full' items={page?.modularity?.items?.[0]?.buttonCollection?.items} />
            </div>
          </>
        }
      />

      <VideoTextPrimary
        id={page?.flexibility?.items?.[0]?.scrollTo?.slice(1)}
        className='my-[0]!'
        videoClassName='mt-auto order-1 lg:order-1 lg:w-5/12'
        textContainerClassName='lg:w-6/12 '
        videoURL={page?.flexibility?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.flexibility?.items?.[0]?.preTitle}
        title={page?.flexibility?.items?.[0]?.title}
        titleClassName='lg:max-w-[55rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[60rem]'
              bodyCopy={page?.flexibility?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />
      <div className='bg-background-tertiary'>
        <VideoTextPrimary
          id={page?.testAndTrain?.items?.[0]?.scrollTo?.slice(1)}
          className='gap-x-[6.7rem]! py-[6rem] lg:py-[0rem]'
          videoClassName='order-0! lg:w-5/12'
          textContainerClassName='lg:w-6/12 [&>header]:py-[0rem]!'
          videoURL={page?.testAndTrain?.items?.[0]?.mediaPrimary?.url}
          title={page?.testAndTrain?.items?.[0]?.title}
          titleClassName='lg:max-w-[55rem]'
          pretitle={page?.testAndTrain?.items?.[0]?.preTitle}
          childrenElements={
            <>
              <RenderBodyCopy
                className='max-w-[51.7rem] text-left 2xl:max-w-[60rem]'
                bodyCopy={page?.testAndTrain?.items?.[0]?.bodyCopy?.json?.content}
              />
            </>
          }
        />
      </div>

      <Container attr='this-is-valdhub' id={page?.trainingMode?.items?.[0]?.scrollTo?.slice(1)}>
        <SectionHeader
          className='lg:mb-[5rem]'
          title={page?.trainingMode?.items?.[0]?.title}
          pretitle={page?.trainingMode?.items?.[0]?.preTitle}
          titleClassName='mb-[2rem] lg:mb-[unset] text-[2.8rem] font-light leading-[3.2rem] md:leading-[3.2rem] md:mb-[3.2rem] xl:text-[4.8rem] xl:leading-[5rem] 2xl:max-w-[90rem]'
          align='center'
        >
          <RenderBodyCopy bodyCopy={page?.trainingMode?.items?.[0]?.bodyCopy?.json?.content} />
        </SectionHeader>

        <HotSpots
          className='3xl:[&_figure]:left-[0%]! mx-auto mb-[8rem] p-[0]! md:mb-[14rem] lg:w-[80%] xl:w-[70%] [&_.box-float]:p-[0.5rem] [&_.box-float_h3]:text-[1.6rem] [&_.box-float_p]:text-[1.6rem]'
          data={page?.trainingMode?.items?.[0]}
        />
      </Container>

      <Container
        id={page?.trainingModeVideo?.items?.[0]?.scrollTo?.slice(1)}
        className={`flex flex-col gap-[3.2rem] lg:flex-row lg:items-center`}
      >
        <div className={`lg:relative lg:z-20 lg:w-[40%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.trainingModeVideo?.items?.[0]?.preTitle}
            title={page?.trainingModeVideo?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy
              className='max-w-[52rem]'
              bodyCopy={page?.trainingModeVideo?.items?.[0]?.bodyCopy?.json?.content}
            />
          </SectionHeader>
        </div>

        {/* TODO Swap to VideoEmbedPlayer component once training mode vide is update */}
        <div className='relative aspect-video w-full overflow-hidden lg:relative lg:z-20 lg:order-1 lg:h-[50rem]'>
          <iframe
            className='absolute inset-0 block h-full w-full border-0'
            src={`${page?.trainingModeVideo?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            allowFullScreen
            allow='autoplay; fullscreen; encrypted-media; picture-in-picture; loop;'
            title='Video player'
          />
        </div>
      </Container>

      <Container
        id={page?.strongAndVersatile?.items?.[0]?.scrollTo?.slice(1)}
        className='md:flex md:flex-col lg:py-[4rem]'
      >
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.strongAndVersatile?.items?.[0]?.preTitle}
          title={page?.strongAndVersatile?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] lg:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] lg:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:max-w-[50rem] lg:max-w-[55rem]'
              bodyCopy={page?.strongAndVersatile?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='lg:justify-center'
              items={page?.strongAndVersatile?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        <InlineSVG
          className='mt-[4rem] md:hidden'
          src={page?.strongAndVersatile?.items?.[0]?.mediaTertiary?.url}
          renderAt={['mobile']}
        />

        <InlineSVG
          className='mb-[4rem] hidden md:block lg:hidden'
          src={page?.strongAndVersatile?.items?.[0]?.mediaSecondary?.url}
          renderAt={['tablet']}
        />

        <InlineSVG
          className='hidden lg:block'
          src={page?.strongAndVersatile?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop']}
        />
      </Container>

      <Container className='flex flex-col justify-between md:gap-[4rem] 2xl:flex-row-reverse 2xl:gap-[0]'>
        <div
          id={page?.forceframeMax?.items?.[0]?.scrollTo?.slice(1)}
          className='flex flex-col-reverse justify-between md:flex-row-reverse md:items-center 2xl:w-[50%] 2xl:flex-col-reverse'
        >
          {/* FF MAX */}
          <ProductBox
            className='text-left! sm:text-center md:my-[unset] md:max-w-[46rem]! lg:relative xl:self-end!'
            buttonClassName='justify-start! sm:gap-[1rem]'
            product={page?.forceframeMax?.items?.[0]}
          />
          <NextImage
            className='md:left-[-2rem] md:scale-[1.1] lg:w-[50%] lg:scale-[1] 2xl:bottom-[-7rem] 2xl:w-full'
            src={page?.forceframeMax?.items?.[0]?.mediaPrimary?.url}
            alt={page?.forceframeMax?.items?.[0]?.mediaPrimary?.description}
            aspectRatio={
              page?.forceframeMax?.items?.[0]?.mediaPrimary?.width /
              page?.forceframeMax?.items?.[0]?.mediaPrimary?.height
            }
            width={page?.forceframeMax?.items?.[0]?.mediaPrimary?.width}
          />
        </div>

        {/* FF FOLD */}
        <div
          id={page?.forceframeFold?.items?.[0]?.scrollTo?.slice(1)}
          className='flex flex-col-reverse justify-between md:flex-row md:items-center 2xl:w-[50%] 2xl:flex-col'
        >
          <ProductBox
            className='xl:locale-es:top-[-0.7rem] xl:locale-fr:top-[-2.7rem] relative! z-1! text-left! sm:text-center md:max-w-[46rem]! lg:relative xl:top-[2.7rem] xl:self-start! xl:mix-blend-darken'
            buttonClassName='justify-start! sm:gap-[1rem]'
            product={page?.forceframeFold?.items?.[0]}
          />

          <NextImage
            className='md:scale-[1.1] lg:w-[50%] lg:scale-[1] 2xl:top-[-10rem] 2xl:w-full'
            src={page?.forceframeFold?.items?.[0]?.mediaPrimary?.url}
            alt={page?.forceframeFold?.items?.[0]?.mediaPrimary?.description}
            aspectRatio={
              page?.forceframeFold?.items?.[0]?.mediaPrimary?.width /
              page?.forceframeFold?.items?.[0]?.mediaPrimary?.height
            }
            width={page?.forceframeFold?.items?.[0]?.mediaPrimary?.width}
          />
        </div>
      </Container>

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mt-[0]! py-[8rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          headerImgL='w-[11.5rem] lg:w-2xs h-full mt-[1rem] lg:mt-[-4rem] lg:ml-[-1rem]'
          headerImgR='w-[9rem] lg:w-[12rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <Faq faq={page?.faq?.items?.[0]} />

      <FeaturedProducts className='mt-[8rem] [&>header>h2]:max-w-[60rem]' data={page?.valdSystems?.items?.[0]} />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default ForceFramePageClient;
